// src/lib/services/audit.ts

import type { AuditEntry, AuditAction } from '@/lib/types/audit';
import { get } from '@/lib/api/client';

interface RawAuditEntry {
  id: string;
  timestamp: string;
  workflow_id: string;
  from_status: string;
  to_status: string;
  actor: string;
  action: string;
  booking_id: string | null;
  actor_email: string | null;
  notes: string | null;
  sender_email: string | null;
  subject: string | null;
}

function mapAuditEntry(raw: RawAuditEntry): AuditEntry {
  return {
    id: raw.id,
    timestamp: raw.timestamp,
    workflowId: raw.workflow_id,
    fromStatus: raw.from_status,
    toStatus: raw.to_status,
    actorEmail: raw.actor_email ?? raw.actor,
    action: raw.action as AuditAction,
    bookingId: raw.booking_id ?? raw.workflow_id,
    notes: raw.notes ?? undefined,
    senderEmail: raw.sender_email ?? undefined,
    subject: raw.subject ?? undefined,
  };
}

export async function getAuditLog(limit: number = 200): Promise<AuditEntry[]> {
  const raw = await get<RawAuditEntry[]>(`/api/v1/audit?limit=${limit}`);
  return raw.map(mapAuditEntry);
}

/**
 * Collapse a list of audit entries down to one entry per booking — the
 * most recent transition for that booking. Assumes `entries` is already
 * ordered newest-first, which is what the backend's default (no
 * `workflow_id` filter) `GET /api/v1/audit` query returns.
 *
 * This is a display-only change: the audit-service backend stays fully
 * append-only/immutable (every historical transition is still persisted,
 * and still retrievable in full via `GET /api/v1/audit?workflow_id=...`).
 * It only changes what the dashboard's Audit Log tab shows, so an
 * operator sees each booking's current state once instead of a growing
 * pile of rows for every state it passed through on the way there.
 * Requested 17 Sep 2026.
 */
export function latestPerBooking(entries: AuditEntry[]): AuditEntry[] {
  const seen = new Set<string>();
  const result: AuditEntry[] = [];
  for (const entry of entries) {
    if (seen.has(entry.bookingId)) continue;
    seen.add(entry.bookingId);
    result.push(entry);
  }
  return result;
}
